import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, typography, textStyles, spacing } from '../../theme';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface EventCardProps {
  event: {
    _id?: string;
    title: string;
    description?: string;
    startDate: string | Date;
    endDate: string | Date;
    timezone?: string;
    isAllDay?: boolean;
    meetingType?: 'online' | 'offline' | 'hybrid';
    location?: {
      name?: string;
      address?: string;
    };
    onlineMeeting?: {
      provider?: string;
      joinUrl?: string;
    };
    eventStatus?: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
    attendees?: Array<{ type: string; status: string }>;
  };
  onPress?: (eventId: string) => void;
  onEdit?: (eventId: string) => void;
  onDelete?: (eventId: string) => void;
  showActions?: boolean;
  containerStyle?: any;
}

export const EventCard: React.FC<EventCardProps> = ({
  event,
  onPress,
  onEdit,
  onDelete,
  showActions = true,
  containerStyle,
}) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();

  const handlePress = () => {
    if (onPress && event._id) {
      onPress(event._id);
    }
  };

  const handleEdit = (e: any) => {
    e?.stopPropagation();
    if (onEdit && event._id) {
      onEdit(event._id);
    }
  };

  const handleDelete = (e: any) => {
    e?.stopPropagation();
    if (onDelete && event._id) {
      onDelete(event._id);
    }
  };

  const formatDate = (date: string | Date) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (date: string | Date) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const startDate = typeof event.startDate === 'string' ? new Date(event.startDate) : event.startDate;
  const endDate = typeof event.endDate === 'string' ? new Date(event.endDate) : event.endDate;
  const isSameDay = startDate.toDateString() === endDate.toDateString();

  const getStatusColor = () => {
    switch (event.eventStatus) {
      case 'completed':
        return colors.success;
      case 'in_progress':
        return colors.primary;
      case 'cancelled':
        return colors.error;
      default:
        return colors.textLight;
    }
  };

  const attendeeCount = event.attendees?.length || 0;
  const acceptedCount = event.attendees?.filter(a => a.status === 'accepted').length || 0;

  return (
    <TouchableOpacity
      style={[styles.container, containerStyle]}
      onPress={handlePress}
      activeOpacity={0.7}
    >
      <View style={[styles.content, { flexDirection: rtl.rowDirection }]}>
        {/* Date/Time Indicator */}
        <View style={[styles.dateContainer, rtl.marginEnd(spacing.md), rtl.paddingEnd(spacing.md), rtl.isRTL ? { borderLeftWidth: 1, borderLeftColor: colors.border, borderRightWidth: 0 } : {}]}>
          <Text style={styles.month}>{formatDate(startDate).split(' ')[0]}</Text>
          <Text style={styles.day}>{startDate.getDate()}</Text>
          {!event.isAllDay && (
            <Text style={styles.time}>{formatTime(startDate)}</Text>
          )}
        </View>

        {/* Event Info */}
        <View style={styles.infoContainer}>
          <View style={[styles.headerRow, { flexDirection: rtl.rowDirection }]}>
            <Text style={styles.title} numberOfLines={1}>
              {event.title}
            </Text>
            {event.eventStatus && event.eventStatus !== 'scheduled' && (
              <View style={[styles.statusBadge, { backgroundColor: getStatusColor() + '20' }]}>
                <Text style={[styles.statusText, { color: getStatusColor() }]}>
                  {event.eventStatus.replace('_', ' ')}
                </Text>
              </View>
            )}
          </View>

          {event.description && (
            <Text style={styles.description} numberOfLines={2}>
              {event.description}
            </Text>
          )}

          <View style={[styles.metaRow, { flexDirection: rtl.rowDirection }]}>
            {event.isAllDay ? (
              <View style={[styles.metaItem, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="calendar-outline" size={14} color={colors.textLight} />
                <Text style={styles.metaText}>{t('calendar.eventCard.allDay')}</Text>
              </View>
            ) : (
              <>
                <View style={[styles.metaItem, { flexDirection: rtl.rowDirection }]}>
                  <Ionicons name="time-outline" size={14} color={colors.textLight} />
                  <Text style={styles.metaText}>
                    {formatTime(startDate)} - {formatTime(endDate)}
                  </Text>
                </View>
                {!isSameDay && (
                  <View style={[styles.metaItem, { flexDirection: rtl.rowDirection }]}>
                    <Ionicons name="calendar-outline" size={14} color={colors.textLight} />
                    <Text style={styles.metaText}>{t('calendar.eventCard.until', { date: formatDate(endDate) })}</Text>
                  </View>
                )}
              </>
            )}

            {event.meetingType === 'online' && event.onlineMeeting?.joinUrl && (
              <View style={[styles.metaItem, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="videocam-outline" size={14} color={colors.primary} />
                <Text style={[styles.metaText, { color: colors.primary }]}>{t('calendar.eventCard.online')}</Text>
              </View>
            )}

            {event.meetingType === 'offline' && event.location?.name && (
              <View style={[styles.metaItem, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="location-outline" size={14} color={colors.textLight} />
                <Text style={styles.metaText} numberOfLines={1}>
                  {event.location.name}
                </Text>
              </View>
            )}

            {attendeeCount > 0 && (
              <View style={[styles.metaItem, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="people-outline" size={14} color={colors.textLight} />
                <Text style={styles.metaText}>
                  {t('calendar.eventCard.acceptedCount', { accepted: acceptedCount, total: attendeeCount })}
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Actions */}
        {showActions && (onEdit || onDelete) && (
          <View style={[styles.actionsContainer, { flexDirection: rtl.rowDirection }, rtl.marginStart(spacing.sm)]}>
            {onEdit && (
              <TouchableOpacity
                style={styles.actionButton}
                onPress={handleEdit}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons name="create-outline" size={20} color={colors.primary} />
              </TouchableOpacity>
            )}
            {onDelete && (
              <TouchableOpacity
                style={styles.actionButton}
                onPress={handleDelete}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons name="trash-outline" size={20} color={colors.error} />
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    backgroundColor: colors.background.surface,
    borderRadius: 12,
    marginBottom: spacing.md,
    shadowColor: colors.literal.black,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  content: {
    flexDirection: 'row',
    padding: spacing.md,
  },
  dateContainer: {
    width: 60,
    alignItems: 'center',
    justifyContent: 'center',
    borderRightWidth: 1,
    borderRightColor: colors.border,
  },
  month: {
    ...textStyles.caption,
    color: colors.textLight,
    fontSize: 11,
    textTransform: 'uppercase',
    fontWeight: '600',
  },
  day: {
    ...textStyles.h2,
    color: colors.text.primary,
    fontSize: 24,
    fontWeight: '700',
    marginVertical: 2,
  },
  time: {
    ...textStyles.caption,
    color: colors.textLight,
    fontSize: 10,
  },
  infoContainer: {
    flex: 1,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs,
    gap: spacing.xs,
  },
  title: {
    ...textStyles.h3,
    color: colors.text.primary,
    flex: 1,
  },
  statusBadge: {
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: 8,
  },
  statusText: {
    ...textStyles.caption,
    fontSize: 10,
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  description: {
    ...textStyles.body,
    color: colors.textLight,
    marginBottom: spacing.xs,
    fontSize: 13,
  },
  metaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
    marginTop: spacing.xs,
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metaText: {
    ...textStyles.caption,
    color: colors.textLight,
    fontSize: 12,
  },
  actionsContainer: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  actionButton: {
    padding: spacing.xs,
  },
});

